/**
 * Tax organizers: a questionnaire whose answers decide which documents and
 * questions land on the client's checklist. The same pure functions run in the
 * browser (to show/hide questions) and on the server (the source of truth).
 */
import { z } from "zod";

const key = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, "Keys use lowercase letters, digits and _");

export const conditionSchema = z.object({
  key,
  op: z.enum(["eq", "neq", "gt", "answered"]),
  value: z.union([z.string().max(100), z.number(), z.boolean()]).optional(),
}).strict();
export type Condition = z.infer<typeof conditionSchema>;

/** A checklist item created when a question's answer matches `when`. */
export const ruleSchema = z.object({
  when: conditionSchema.omit({ key: true }).default({ op: "eq", value: true }),
  type: z.enum(["document", "question"]),
  // "{year}" / "{prev_year}" become the tax year and the year before; "{n}" the item number for counts.
  title: z.string().trim().min(1).max(200),
  instructions: z.string().trim().max(1000).optional(),
  // For "count" and "group" questions: one item per counted thing or row (e.g. each W-2).
  perItem: z.boolean().default(false),
}).strict();
export type Rule = z.infer<typeof ruleSchema>;

export const QUESTION_TYPES = ["yes_no", "text", "long_text", "number", "money", "date", "choice", "count", "group", "ssn", "ein", "routing", "account"] as const;

/**
 * Sensitive numbers: Social Security numbers, EINs, bank routing and account numbers. Stored as
 * digits only (answers are encrypted), checked for shape, and shown masked to the last four.
 */
export const SENSITIVE_TYPES = ["ssn", "ein", "routing", "account"] as const;

/** The kinds of entry in each row of a repeating group (each W-2, each dependent). */
export const FIELD_TYPES = ["text", "number", "money", "date", "choice", "yes_no", "ssn", "ein", "routing", "account"] as const;

const optionSchema = z.object({ value: z.string().regex(/^[a-z0-9_]{1,40}$/), label: z.string().trim().min(1).max(100) }).strict();

export const fieldSchema = z.object({
  key,
  type: z.enum(FIELD_TYPES),
  label: z.string().trim().min(1).max(120),
  options: z.array(optionSchema).max(30).optional(),
  required: z.boolean().default(false),
}).strict();
export type GroupField = z.infer<typeof fieldSchema>;

export const questionSchema = z.object({
  key,
  type: z.enum(QUESTION_TYPES),
  label: z.string().trim().min(1).max(300),
  help: z.string().trim().max(1000).optional(),
  options: z.array(optionSchema).max(30).optional(),
  required: z.boolean().default(false),
  // A repeating group: one row per item (each W-2, each dependent), each with these fields.
  fields: z.array(fieldSchema).max(15).optional(),
  // What one row is called ("W-2", "dependent"), and the field that names it ("employer").
  itemLabel: z.string().trim().min(1).max(60).optional(),
  titleField: key.optional(),
  maxRows: z.number().int().min(1).max(50).optional(),
  // Copy last year's answer into this year's organizer.
  prefill: z.boolean().default(false),
  // Shown only when every condition matches (conditions may only refer to earlier questions).
  showIf: z.array(conditionSchema).max(5).default([]),
  creates: z.array(ruleSchema).max(5).default([]),
  // Tax years the question applies to (e.g. a credit that ended). Omitted = every year.
  years: z.object({ from: z.number().int().min(2000).max(2100).optional(), to: z.number().int().min(2000).max(2100).optional() }).strict().optional(),
}).strict();
export type Question = z.infer<typeof questionSchema>;

/**
 * Why a condition can never match the question it reads (for example a choice
 * option that was since removed, or "yes" compared with a number), or null.
 */
export function conditionProblem(q: Pick<Question, "type" | "options">, c: Omit<Condition, "key">): string | null {
  if (c.op === "answered") return null;
  const v = c.value;
  if (v === undefined) return "has no value to compare with";
  if (c.op === "gt") return ["number", "money", "count", "group"].includes(q.type) && typeof v === "number" ? null : "is compared as a number but isn't one";
  switch (q.type) {
    case "yes_no":
      return typeof v === "boolean" ? null : "is compared with something other than yes or no";
    case "number":
    case "money":
    case "count":
    case "group":
      return typeof v === "number" ? null : "is compared with something that isn't a number";
    case "choice":
      return typeof v === "string" && (q.options ?? []).some((o) => o.value === v) ? null : `is "${String(v)}", which is no longer one of its options`;
    default:
      return typeof v === "string" ? null : "is compared with something that isn't text";
  }
}

export const organizerDefinitionSchema = z
  .object({
    sections: z
      .array(z.object({ key, title: z.string().trim().min(1).max(120), questions: z.array(questionSchema).min(1).max(60) }).strict())
      .min(1)
      .max(20),
  })
  .strict()
  .superRefine((def, ctx) => {
    const seen = new Map<string, Question>();
    for (const sec of def.sections) {
      for (const q of sec.questions) {
        if (seen.has(q.key)) ctx.addIssue({ code: "custom", message: `Question key "${q.key}" is used twice` });
        if (q.type === "choice" && !(q.options && q.options.length >= 2)) ctx.addIssue({ code: "custom", message: `"${q.key}" needs at least two options` });
        if (q.type !== "choice" && q.options) ctx.addIssue({ code: "custom", message: `"${q.key}" has options but isn't a choice` });
        if (q.type === "group") {
          const fields = q.fields ?? [];
          if (fields.length === 0) ctx.addIssue({ code: "custom", message: `"${q.key}" needs at least one field for each row` });
          if (new Set(fields.map((f) => f.key)).size !== fields.length) ctx.addIssue({ code: "custom", message: `"${q.key}" uses a field key twice` });
          for (const f of fields) {
            if (f.type === "choice" && !(f.options && f.options.length >= 2)) ctx.addIssue({ code: "custom", message: `"${q.key}.${f.key}" needs at least two options` });
            if (f.type !== "choice" && f.options) ctx.addIssue({ code: "custom", message: `"${q.key}.${f.key}" has options but isn't a choice` });
          }
          const title = fields.find((f) => f.key === q.titleField);
          if (q.titleField && !title) ctx.addIssue({ code: "custom", message: `"${q.key}" names its rows by "${q.titleField}", which isn't one of its fields` });
          // Row names appear in checklist titles and comparisons, so they can't be protected numbers.
          if (title && (SENSITIVE_TYPES as readonly string[]).includes(title.type)) ctx.addIssue({ code: "custom", message: `"${q.key}" can't name its rows by a protected number` });
        } else if (q.fields) ctx.addIssue({ code: "custom", message: `"${q.key}" has fields but isn't a group` });
        for (const c of q.showIf) {
          if (!seen.has(c.key)) ctx.addIssue({ code: "custom", message: `"${q.key}" depends on "${c.key}", which must come earlier` });
        }
        for (const r of q.creates) {
          if (r.perItem && q.type !== "count" && q.type !== "group") ctx.addIssue({ code: "custom", message: `"${q.key}": one-per-item only works on count and group questions` });
        }
        seen.set(q.key, q);
      }
    }
    if (seen.size > 300) ctx.addIssue({ code: "custom", message: "Too many questions" });
  });
export type OrganizerDefinition = z.infer<typeof organizerDefinitionSchema>;

/**
 * What a firm may save: also refuses conditions that can never match (an option
 * since removed, "yes" compared with a number). Checked only on save, so forms
 * saved before this rule keep opening.
 */
export const organizerDefinitionInputSchema = organizerDefinitionSchema.superRefine((def, ctx) => {
  const seen = new Map<string, Question>();
  for (const sec of def.sections) {
    for (const q of sec.questions) {
      for (const c of q.showIf) {
        const dep = seen.get(c.key);
        const bad = dep && conditionProblem(dep, c);
        if (bad) ctx.addIssue({ code: "custom", message: `"${q.label}" is shown when "${dep.label}" ${bad}` });
      }
      for (const r of q.creates) {
        // One-per-item rules ignore `when`; the count decides.
        const bad = r.perItem ? null : conditionProblem(q, r.when);
        if (bad) ctx.addIssue({ code: "custom", message: `"${q.label}" adds "${r.title}" when the answer ${bad}` });
      }
      seen.set(q.key, q);
    }
  }
});

export type Scalar = string | number | boolean;
/** One row of a repeating group, by field key. */
export type GroupRow = Record<string, Scalar>;
export type AnswerValue = Scalar | GroupRow[];
export type Answers = Record<string, AnswerValue>;

/** A group's answer as rows (anything else reads as none). */
export const rowsOf = (v: AnswerValue | undefined): GroupRow[] => (Array.isArray(v) ? v : []);

/** Largest count honored for per-item checklist entries (e.g. W-2s). */
export const MAX_PER_ITEM = 20;

function matches(c: Omit<Condition, "key">, raw: AnswerValue | undefined): boolean {
  // A group is compared by how many rows it has.
  const v = Array.isArray(raw) ? (raw.length ? raw.length : undefined) : raw;
  switch (c.op) {
    case "answered":
      return v !== undefined && v !== "";
    case "eq":
      return v === c.value;
    case "neq":
      return v !== undefined && v !== c.value;
    case "gt":
      return typeof v === "number" && typeof c.value === "number" && v > c.value;
  }
}

/** Questions currently shown, in order. A question hidden by a hidden question is hidden too. */
export function visibleQuestions(def: OrganizerDefinition, answers: Answers): Question[] {
  const visible = new Set<string>();
  const out: Question[] = [];
  for (const sec of def.sections) {
    for (const q of sec.questions) {
      const shown = q.showIf.every((c) => visible.has(c.key) && matches(c, answers[c.key]));
      if (shown) {
        visible.add(q.key);
        out.push(q);
      }
    }
  }
  return out;
}

/**
 * A Social Security number, or an ITIN (starts with 9, middle digits 50–65, 70–88, 90–92 or 94–99),
 * which spouses and dependents without an SSN use.
 */
export function validTaxpayerId(d: string): boolean {
  if (!/^\d{9}$/.test(d)) return false;
  if (d.startsWith("9")) {
    const g = Number(d.slice(3, 5));
    return (g >= 50 && g <= 65) || (g >= 70 && g <= 88) || (g >= 90 && g <= 92) || g >= 94;
  }
  return !d.startsWith("000") && !d.startsWith("666") && d.slice(3, 5) !== "00" && d.slice(5) !== "0000";
}

/** The ABA checksum every US bank routing number passes. */
export function validRouting(digits: string): boolean {
  if (!/^\d{9}$/.test(digits)) return false;
  const d = digits.split("").map(Number);
  return (3 * (d[0]! + d[3]! + d[6]!) + 7 * (d[1]! + d[4]! + d[7]!) + (d[2]! + d[5]! + d[8]!)) % 10 === 0;
}

type ScalarSpec = { type: (typeof FIELD_TYPES)[number] | "long_text" | "count"; options?: Array<{ value: string }> };

type Problem = { problem: string };
const isProblem = (v: unknown): v is Problem => typeof v === "object" && v !== null && !Array.isArray(v) && "problem" in v;
const no = (problem: string): Problem => ({ problem });

// Returns the cleaned value, or the problem with it.
function checkScalar(q: ScalarSpec, v: unknown): Scalar | Problem | undefined {
  if (v === null || v === undefined || v === "") return undefined;
  const digits = typeof v === "string" ? v.replace(/[\s-]/g, "") : "";
  switch (q.type) {
    case "yes_no":
      return typeof v === "boolean" ? v : no("Answer yes or no");
    case "text":
      return typeof v === "string" && v.trim().length <= 500 ? v.trim() : no("Keep it under 500 characters");
    case "long_text":
      return typeof v === "string" && v.trim().length <= 5000 ? v.trim() : no("Keep it under 5,000 characters");
    case "number":
      return typeof v === "number" && Number.isFinite(v) && Math.abs(v) <= 1e12 ? v : no("Enter a number");
    case "money":
      // Whole cents only, so amounts are never rounded.
      return typeof v === "number" && Number.isSafeInteger(v) && Math.abs(v) <= 1e13 ? v : no("Enter an amount in cents");
    case "count":
      return typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 99 ? v : no("Enter a whole number from 0 to 99");
    case "date":
      return typeof v === "string" && isRealDate(v) ? v : no("Enter a real date (YYYY-MM-DD)");
    case "choice":
      return typeof v === "string" && (q.options ?? []).some((o) => o.value === v) ? v : no("Pick one of the options");
    case "ssn":
      return validTaxpayerId(digits) ? digits : no("Enter all 9 digits of the Social Security number or ITIN");
    case "ein":
      return /^\d{9}$/.test(digits) ? digits : no("Enter all 9 digits of the EIN");
    case "routing":
      return validRouting(digits) ? digits : no("Check the routing number: it should be the 9 digits at the bottom left of a check");
    case "account":
      return /^\d{4,17}$/.test(digits) ? digits : no("Enter the account number (4 to 17 digits)");
  }
}

function checkValue(q: Question, v: unknown): AnswerValue | Problem | undefined {
  if (q.type !== "group") return checkScalar(q as ScalarSpec, v);
  if (v === null || v === undefined) return undefined;
  if (!Array.isArray(v)) return no("Enter each item");
  const max = q.maxRows ?? 20;
  if (v.length > max) return no(`Enter at most ${max}`);
  const rows: GroupRow[] = [];
  for (const [i, raw] of v.entries()) {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return no(`Item ${i + 1} isn't complete`);
    const row: GroupRow = {};
    for (const f of q.fields ?? []) {
      const c = checkScalar(f, (raw as Record<string, unknown>)[f.key]);
      if (isProblem(c)) return no(`${q.itemLabel ?? "Item"} ${i + 1}, ${f.label}: ${c.problem}`);
      if (c !== undefined) row[f.key] = c;
    }
    // A row left entirely blank is dropped rather than stored.
    if (Object.keys(row).length) rows.push(row);
  }
  return rows;
}

/**
 * Keeps only answers to questions in the definition, cleaned and type-checked.
 * Answers to hidden questions are kept while drafting (the client may flip a
 * yes/no back) but ignored by `checklistFor` and `missingRequired`.
 */
export function cleanAnswers(def: OrganizerDefinition, raw: Record<string, unknown>): { answers: Answers; errors: Record<string, string> } {
  const answers: Answers = {};
  const errors: Record<string, string> = {};
  for (const sec of def.sections) {
    for (const q of sec.questions) {
      if (!(q.key in raw)) continue;
      const v = checkValue(q, raw[q.key]);
      if (isProblem(v)) errors[q.key] = v.problem;
      else if (v !== undefined) answers[q.key] = v;
    }
  }
  return { answers, errors };
}

export function missingRequired(def: OrganizerDefinition, answers: Answers): string[] {
  return visibleQuestions(def, answers)
    .filter((q) => {
      const v = answers[q.key];
      if (q.type === "group") {
        const rows = rowsOf(v);
        // Every row needs its required fields; a required group needs at least one row.
        return (q.required && rows.length === 0) || rows.some((r) => (q.fields ?? []).some((f) => f.required && (r[f.key] === undefined || r[f.key] === "")));
      }
      return q.required && (v === undefined || v === "");
    })
    .map((q) => q.key);
}

export type ChecklistItem = { sourceKey: string; type: "document" | "question"; title: string; instructions?: string };

/** True for an existing calendar date: rejects 2026-02-30, which Date.parse would roll over. */
export function isRealDate(v: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d;
}

/**
 * The questions that apply to one tax year. Questions limited to other years are
 * removed, along with any question that depends on a removed one.
 */
export function forTaxYear(def: OrganizerDefinition, taxYear: number): OrganizerDefinition {
  const removed = new Set<string>();
  const sections = def.sections
    .map((sec) => ({
      ...sec,
      questions: sec.questions.filter((q) => {
        const out = (q.years?.from !== undefined && taxYear < q.years.from) || (q.years?.to !== undefined && taxYear > q.years.to) || q.showIf.some((c) => removed.has(c.key));
        if (out) removed.add(q.key);
        return !out;
      }),
    }))
    .filter((sec) => sec.questions.length > 0);
  return { sections };
}

/** Fills "{year}" and "{prev_year}" in labels and titles. */
export function fillYear(text: string, taxYear: number): string {
  return text.replaceAll("{prev_year}", String(taxYear - 1)).replaceAll("{year}", String(taxYear));
}

/** The checklist the answers call for. `sourceKey` is stable, so re-submitting never duplicates items. */
export function checklistFor(def: OrganizerDefinition, answers: Answers, taxYear: number): ChecklistItem[] {
  const items: ChecklistItem[] = [];
  const fill = (s: string, n?: number) => fillYear(s, taxYear).replaceAll("{n}", n === undefined ? "" : String(n));
  for (const q of visibleQuestions(def, answers)) {
    const v = answers[q.key];
    q.creates.forEach((rule, i) => {
      if (rule.perItem && q.type === "group") {
        // One item per row, named after the row ("W-2 from Acme Corp") and keyed by that name, so
        // removing or reordering rows leaves each item with its own employer.
        const used = new Map<string, number>();
        rowsOf(v)
          .slice(0, MAX_PER_ITEM)
          .forEach((row, k) => {
            const titled = q.titleField && row[q.titleField] !== undefined && row[q.titleField] !== "";
            const name = titled ? String(row[q.titleField!]) : String(k + 1);
            const base = titled ? `=${normName(name).slice(0, 80)}` : `#${k + 1}`;
            const seen = (used.get(base) ?? 0) + 1;
            used.set(base, seen);
            const f = (t: string) => fill(t, k + 1).replaceAll("{name}", name).trim();
            items.push({ sourceKey: `${q.key}.${i}.${base}${seen > 1 ? `~${seen}` : ""}`, type: rule.type, title: f(rule.title), instructions: rule.instructions && f(rule.instructions) });
          });
      } else if (rule.perItem) {
        const n = typeof v === "number" ? Math.min(v, MAX_PER_ITEM) : 0;
        for (let k = 1; k <= n; k++) {
          items.push({ sourceKey: `${q.key}.${i}.${k}`, type: rule.type, title: fill(rule.title, k).trim(), instructions: rule.instructions && fill(rule.instructions, k) });
        }
      } else if (matches(rule.when, v)) {
        items.push({ sourceKey: `${q.key}.${i}`, type: rule.type, title: fill(rule.title).trim(), instructions: rule.instructions && fill(rule.instructions) });
      }
    });
  }
  return items;
}

const normName = (x: string) => x.trim().toLowerCase().replace(/\s+/g, " ");

/** Last year's answers to carry forward (only questions marked `prefill`). */
export function prefillAnswers(def: OrganizerDefinition, previous: Answers): Answers {
  const out: Answers = {};
  for (const sec of def.sections) for (const q of sec.questions) if (q.prefill && q.key in previous) out[q.key] = previous[q.key]!;
  return cleanAnswers(def, out).answers;
}

// --- Showing and comparing answers ------------------------------------------------------------

const isSensitive = (type: string) => (SENSITIVE_TYPES as readonly string[]).includes(type);

/** "•••-••-6789": a sensitive number shown to its last four digits. */
export function maskSensitive(type: string, digits: string): string {
  const last = digits.slice(-4);
  if (type === "ssn") return `•••-••-${last}`;
  if (type === "ein") return `••-•••${last}`;
  return `••••${last}`;
}

/** A sensitive number written the usual way: 123-45-6789, 12-3456789. */
function formatSensitive(type: string, d: string): string {
  if (type === "ssn" && d.length === 9) return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  if (type === "ein" && d.length === 9) return `${d.slice(0, 2)}-${d.slice(2)}`;
  return d;
}

type Spec = { type: string; options?: Array<{ value: string; label: string }> };

/** One value as people read it ("$1,234.56", "Yes", the option's label); sensitive numbers masked unless `reveal`. */
export function formatValue(spec: Spec, v: Scalar | undefined, reveal = false): string {
  if (v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (spec.type === "money" && typeof v === "number") return `${v < 0 ? "-" : ""}$${(Math.abs(v) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (spec.type === "choice") return spec.options?.find((o) => o.value === v)?.label ?? String(v);
  if (isSensitive(spec.type)) return reveal ? formatSensitive(spec.type, String(v)) : maskSensitive(spec.type, String(v));
  return String(v);
}

/** Whether a question has an answer (a list counts once it has an entry). */
export function isAnswered(v: AnswerValue | undefined): boolean {
  return v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
}

/** The answers with every protected number masked to its last four digits ("•••-••-6789"). */
export function maskAnswers(def: OrganizerDefinition, answers: Answers): Answers {
  const out: Answers = { ...answers };
  for (const sec of def.sections) {
    for (const q of sec.questions) {
      const v = answers[q.key];
      if (v === undefined) continue;
      if (q.type === "group") {
        const hidden = (q.fields ?? []).filter((f) => isSensitive(f.type));
        if (hidden.length) out[q.key] = rowsOf(v).map((row) => Object.fromEntries(Object.entries(row).map(([k, x]) => [k, hidden.some((f) => f.key === k) && x !== undefined ? maskSensitive(hidden.find((f) => f.key === k)!.type, String(x)) : x])));
      } else if (isSensitive(q.type)) out[q.key] = maskSensitive(q.type, String(v));
    }
  }
  return out;
}

/** A group row's name: its title field, or "W-2 2". */
export function rowName(q: Question, row: GroupRow, index: number): string {
  const t = q.titleField ? row[q.titleField] : undefined;
  if (t === undefined || t === "") return `${q.itemLabel ?? "Item"} ${index + 1}`;
  const spec = q.fields?.find((f) => f.key === q.titleField);
  return spec && isSensitive(spec.type) ? maskSensitive(spec.type, String(t)) : String(t);
}

/**
 * What changed since last year's answers, in plain sentences for the preparer: standing facts
 * (the questions carried forward each year) whose yes/no, choice or count changed, and rows
 * (employers, dependents, properties) that were there last year and aren't now, or are new.
 * Last year's answers that no longer fit the question (the form changed) are left out.
 */
export function changesSinceLastYear(def: OrganizerDefinition, now: Answers, before: Answers, taxYear: number): { changed: string[]; notListed: string[]; added: string[] } {
  const changed: string[] = [];
  const notListed: string[] = [];
  const added: string[] = [];
  for (const q of visibleQuestions(def, now)) {
    const raw = before[q.key];
    const b = now[q.key];
    if (!q.prefill || raw === undefined || isSensitive(q.type)) continue;
    const a = checkValue(q, raw);
    if (a === undefined || isProblem(a)) continue;
    if (q.type === "group") {
      const names = (rows: GroupRow[]) => rows.map((r, i) => rowName(q, r, i));
      const was = names(rowsOf(a));
      const is = names(rowsOf(b));
      const norm = (x: string) => x.trim().toLowerCase();
      for (const n of was) if (!is.some((x) => norm(x) === norm(n))) notListed.push(`${q.itemLabel ?? "Item"}: ${n}`);
      for (const n of is) if (!was.some((x) => norm(x) === norm(n))) added.push(`${q.itemLabel ?? "Item"}: ${n}`);
    } else if (["yes_no", "choice", "count"].includes(q.type) && b !== undefined && a !== b) {
      changed.push(`${fillYear(q.label, taxYear)}: ${formatValue(q, a as Scalar)} last year, ${formatValue(q, b as Scalar)} now`);
    }
  }
  return { changed, notListed, added };
}

/** A value for export: like `formatValue` revealed, but amounts as plain numbers ("-1234.56") that tax software and spreadsheets read. */
function exportValue(spec: Spec, v: Scalar | undefined): string {
  if (spec.type === "money" && typeof v === "number") return (v / 100).toFixed(2);
  return formatValue(spec, v, true);
}

/**
 * Every answer as a flat list, in form order: group rows become "w2s[1].employer". This is the
 * shape exports and tax software mapping read; sensitive numbers are included in full.
 */
export function flattenAnswers(def: OrganizerDefinition, answers: Answers, taxYear: number): Array<{ section: string; key: string; label: string; value: string }> {
  const out: Array<{ section: string; key: string; label: string; value: string }> = [];
  const shown = new Set(visibleQuestions(def, answers).map((q) => q.key));
  for (const sec of def.sections) {
    for (const q of sec.questions) {
      if (!shown.has(q.key) || answers[q.key] === undefined) continue;
      if (q.type === "group") {
        rowsOf(answers[q.key]).forEach((row, i) => {
          for (const f of q.fields ?? []) {
            if (row[f.key] === undefined) continue;
            out.push({ section: sec.title, key: `${q.key}[${i + 1}].${f.key}`, label: fillYear(`${q.itemLabel ?? q.label} ${i + 1}: ${f.label}`, taxYear), value: exportValue(f, row[f.key]) });
          }
        });
      } else {
        out.push({ section: sec.title, key: q.key, label: fillYear(q.label, taxYear), value: exportValue(q, answers[q.key] as Scalar) });
      }
    }
  }
  return out;
}
